/**
 * 스캔 확정/실패의 **근거**를 GA4 파라미터로 옮기는 순수 함수.
 *
 * 왜 따로 빼나 — peeka 의 제품 품질은 "카드를 맞게 확정했는가" 하나이고, 그 판정은
 * ScanPage.confirmScan 안에 흩어진 조건들이 결정한다. 그 조건과 집계용 라벨이 갈리면
 * 대시보드가 조용히 거짓말을 한다. 여기 모아 두고 테스트로 고정한다.
 *
 * ⚠ 여기 라벨은 confirmScan 의 수락 규칙과 **1:1 로 유지**할 것. 수락 조건을 고치면
 *   이 파일과 scanTelemetry.test.ts 도 같이 고친다.
 */
import type { ScanEvidence } from "../../lib/tracking";

/**
 * 확정 근거 판정에 필요한 최소 형태 — ScanPage 의 매치 결과에서 이 필드들만 읽는다.
 * 구조적 타입으로 둬서 결과 타입이 커져도 여기가 안 따라 커지게 한다.
 */
export interface ScanConfirmSignals {
  matched?: boolean;
  /** OCR 컬렉터 번호 유일 일치로 확정 */
  numberResolved?: boolean;
  /** OCR 카드명 + 번호 동시 일치 — 독립 신호 2개라 유사도 격차 제한 없이 승격된다 */
  nameNumberResolved?: boolean;
  /** 카탈로그 확장검색이 OCR 번호로 찾아낸 카드 */
  ocrFound?: { cardSerialNo: number } | null;
  matches?: ReadonlyArray<{ similarity: number }>;
}

/**
 * 어떤 신호가 이 카드를 확정시켰나.
 *
 * 우선순위는 **증거의 강도** 순이다 — 수동 셔터는 사용자가 직접 찍은 것이라 자동 판정
 * 근거와 섞으면 자동 정확도가 부풀려진다(게이트 밖이다). 그 다음은 독립 신호 2개
 * (이름+번호) > 번호 단독 > 임베딩 확신 > 확장검색 순.
 */
export function scanEvidenceOf(
  signals: ScanConfirmSignals | null | undefined,
  manual: boolean,
): ScanEvidence {
  if (manual) return "manual";
  if (signals?.nameNumberResolved) return "name_number";
  if (signals?.numberResolved) return "number";
  if (signals?.matched) return "match";
  return "ocr_found";
}

/**
 * top-1 유사도 — GA4 가 평균을 낼 수 있게 수치로, 소수 2자리로 줄여 보낸다.
 * 후보가 없으면(확장검색으로만 찾은 카드) undefined 라 파라미터 자체가 빠진다.
 */
export function scanSimilarityOf(
  signals: ScanConfirmSignals | null | undefined,
): number | undefined {
  const sim = signals?.matches?.[0]?.similarity;
  if (typeof sim !== "number" || !Number.isFinite(sim)) return undefined;
  return Math.round(sim * 100) / 100;
}
